import { Controller } from "@hotwired/stimulus"
import { showToast } from "../helpers/toast"
import { csrfToken } from "../helpers/instacart_link"

const STORAGE_KEY = "recidex:pending_pantry_moves"

// Crossing items off in a store with no signal. The list is the service
// worker's saved copy, so "Add to Inventory" can't reach the server: the
// items come off the list now, wait in localStorage, and move to the pantry
// through the same move_to_pantry action once the phone is back online.
export default class extends Controller {
  static targets = ["row"]
  static values = { moveUrl: String, listUrl: String }

  connect() {
    if (navigator.onLine) this.sync()
  }

  // A saved page still lists what was crossed off before it was reopened.
  rowTargetConnected(row) {
    if (this.#pendingIds().includes(row.dataset.groceryItemId)) row.remove()
  }

  moveLater(ids) {
    this.#hold(ids)
    this.#toastWaiting(ids.length)
  }

  // Turbo's pantry-form submit failed before reaching the server ("bars but
  // no data" reports online, so grocery-bulk couldn't know to skip it).
  submitFailed(event) {
    event.preventDefault()
    this.moveLater(new FormData(event.target).getAll("grocery_item_ids[]"))
  }

  // False only when the request couldn't get through.
  async sync() {
    const ids = this.#pendingIds()
    if (ids.length === 0) return true
    if (this.syncing) {
      this.resync = true
      return true
    }

    this.syncing = true
    try {
      const response = await fetch(this.moveUrlValue, {
        method: "POST",
        headers: { Accept: "text/vnd.turbo-stream.html", "X-CSRF-Token": csrfToken() },
        body: this.#formData(ids)
      })
      this.#finishSync(ids, response, await response.text())
      return true
    } catch {
      // Still no connection — the next online event or page load retries.
      return false
    } finally {
      this.syncing = false
      if (this.resync) {
        this.resync = false
        this.sync()
      }
    }
  }

  // A redirect is an expired session landing on sign-in, so nothing moved.
  // The 303 (no Location, never followed) means another household member
  // already moved every item; its "Nothing selected." toast would mislead.
  #finishSync(ids, response, body) {
    if (response.redirected || response.status >= 400) {
      showToast("Couldn't move your crossed-off items to your inventory yet. We'll try again.", { variant: "error" })
      return
    }

    this.#savePending(this.#pendingIds().filter((id) => !ids.includes(id)))
    if (response.status !== 303) Turbo.renderStreamMessage(body)
    fetch(this.listUrlValue, { headers: { Accept: "text/html" } }).catch(() => {})
  }

  #hold(ids) {
    this.#savePending([...new Set([...this.#pendingIds(), ...ids.map(String)])])
    this.rowTargets.filter((row) => ids.includes(row.dataset.groceryItemId)).forEach((row) => row.remove())
  }

  #toastWaiting(count) {
    const [word, pronoun] = count === 1 ? ["item", "It'll"] : ["items", "They'll"]
    showToast(`Crossed off ${count} ${word}. ${pronoun} go to your inventory when you're back online.`, { duration: 6000 })
  }

  #formData(ids) {
    const data = new FormData()
    ids.forEach((id) => data.append("grocery_item_ids[]", id))
    return data
  }

  #pendingIds() {
    try {
      return JSON.parse(localStorage.getItem(STORAGE_KEY) || "[]")
    } catch {
      return []
    }
  }

  #savePending(ids) {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(ids))
    } catch {
      // Storage blocked: the crossed-off rows stay gone for this visit only.
    }
  }
}
